// client/src/components/KeyMetrics.js — fixed rows of short facts on a
// mandate, shown as boxes in the CRM mandate header (src/lib/keyMetrics.js):
//   keyMetrics  Key Metrics (migrations/082), set follows Fund / Direct Deal
//   economics   Economics (migrations/083), RGA's terms from the signed EL
// Each value is free text as RGA wants it shown, with an optional source
// note. Staff only.
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { Modal, ErrorBanner } from './people/ui'

const PATH = { keyMetrics: 'key-metrics', economics: 'economics' }

export function useMandateFacts(kind, mandateId) {
  return useQuery({
    queryKey: [PATH[kind], mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/${PATH[kind]}`).then(r => r.data.data),
  })
}

// Label / value rows, laid out like the Taxonomy box.
export function FactRows({ rows }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 14px', alignItems: 'baseline' }}>
      {rows.map(m => [
        <div key={`${m.key}-label`} className="caps-label" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
          {m.label}
        </div>,
        <div key={`${m.key}-value`}>
          <span style={{ color: m.value ? 'var(--text-primary)' : 'var(--text-muted)' }}>{m.value || '--'}</span>
          {m.note && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>{m.note}</span>}
        </div>,
      ])}
    </div>
  )
}

export function EditFactsModal({ kind, title, mandateId, metrics, onClose }) {
  const qc = useQueryClient()
  const [form, setForm] = useState(() => metrics.map(m => ({ key: m.key, label: m.label, value: m.value ?? '', note: m.note ?? '' })))
  const save = useMutation({
    mutationFn: () => api.put(`/api/mandates/${mandateId}/${PATH[kind]}`, {
      metrics: form.map(({ key, value, note }) => ({ key, value, note })),
    }).then(r => r.data.data),
    onSuccess: data => {
      qc.setQueryData([PATH[kind], mandateId], data)
      onClose()
    },
  })
  function set(i, k, v) { setForm(f => f.map((m, j) => j === i ? { ...m, [k]: v } : m)) }

  return (
    <Modal title={title} onClose={onClose} width={680}>
      <form onSubmit={e => { e.preventDefault(); save.mutate() }} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Write each value as it should appear, short. Leave blank to clear. Note: source or as-of date.
        </div>
        {form.map((m, i) => (
          <div key={m.key} style={{ display: 'grid', gridTemplateColumns: '170px 1fr 1fr', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{m.label}</label>
            <input className="input" value={m.value} onChange={e => set(i, 'value', e.target.value)} placeholder="Value" />
            <input className="input" value={m.note} onChange={e => set(i, 'note', e.target.value)} placeholder="Source / as of" />
          </div>
        ))}
        <ErrorBanner msg={save.error?.response?.data?.error ?? save.error?.message} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={save.isPending}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  )
}

// Who RGA has given this mandate's economics to (Travis, 10/27), from the
// partners' economics requests: shared ones, then requests still open.
export function EconomicsAccessModal({ mandateId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['economics-access', mandateId],
    queryFn: () => api.get(`/api/mandates/${mandateId}/economics/access`).then(r => r.data.data),
  })
  const date = iso => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const row = (name, detail, key) => (
    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
      <span style={{ fontWeight: 500 }}>{name}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{detail}</span>
    </div>
  )
  return (
    <Modal title="Economics Access" onClose={onClose} width={480}>
      <div style={{ padding: 24 }}>
        <ErrorBanner msg={error?.response?.data?.error ?? error?.message} />
        {isLoading ? <div className="spinner" /> : (
          <>
            <div className="caps-label" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Partners with access</div>
            {data?.granted?.length
              ? data.granted.map(g => row(g.partner.name, `Given ${date(g.shared_at)}`, g.partner.contact_id))
              : <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>No partner has been given these economics yet.</div>}
            {data?.pending?.length > 0 && (
              <>
                <div className="caps-label" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', margin: '18px 0 4px' }}>Requested, not yet given</div>
                {data.pending.map(p => row(p.partner.name, `Requested ${date(p.requested_at)}`, p.partner.contact_id))}
              </>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 16 }}>
              Access is given from Partner Submissions on the dashboard when a partner requests the economics.
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
